import { Modal } from 'antd';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { pushGenie } from '../../api';
import { GeniePadElementsSettingItem, GenieSettings } from '../../api/pushGenie';
import { getIsPageBlocked, setIsPageBlocked } from '../../App';
import { GenieVersion } from '../../constants';
import { fetchMyGenie } from '../../store/actions/login';
import { getItemFromMisc, MISC_KEYS } from '../../store/idbHandler';
import { track } from '../../tracking';
import { captureError } from '../captureError';
import { his } from '../history';
import GenieConfiguration, { ItemId, MEDICINES } from '../profile/GenieConfiguration';
import { Item } from '../profile/ListGenieItems';
import { getMyDbId } from '../../api/mactiveid';
import { getActiveDoctorId } from '../../store/localstorage';

const BlockHistoryGenie: React.FC<{
    itemId: ItemId;
    items: Item[];
}> = ({ itemId, items }) => {
    const dispatch = useDispatch();
    useEffect(() => {
        const unblock = his.block((tx) => {
            if (!getIsPageBlocked()) return;
            const t = Modal.confirm({
                title: 'Do you want to save changes?',
                okText: 'Save changes',
                cancelText: "Don't save",
                okButtonProps: { className: 'primary-btn font-lato' },
                cancelButtonProps: { className: 'neutral-btn font-lato' },
                visible: true,
                keyboard: true,
                closable: true,
                onOk: async () => {
                    t.update({
                        okButtonProps: {
                            loading: true,
                            className: 'primary-btn font-lato cursor-pointer',
                        },
                    });

                    setTimeout(() => {
                        const el: HTMLDivElement = document.getElementsByClassName(
                            'ant-modal-wrap',
                        )[0] as HTMLDivElement;
                        el?.focus();
                    }, 200);

                    const docId = getActiveDoctorId();
                    const { version } = await getItemFromMisc<GenieVersion>(
                        getMyDbId() || '',
                        MISC_KEYS.genieVersion,
                    );
                    const key = GenieConfiguration.configs.find((i) => i.id === itemId)?.genieKey;

                    if (!docId || !key) {
                        console.error('pushGenieToBE=>', 'docId not found');
                        return;
                    }

                    let newItems = items;
                    if (itemId === MEDICINES) {
                        newItems = newItems.map((i) => ({
                            ...i,
                            suggestions: undefined,
                            currentItemStatus: undefined,
                        }));
                    }

                    try {
                        if (key === 'settings') {
                            const settings = await getItemFromMisc<GenieSettings>(
                                getMyDbId() || '',
                                MISC_KEYS.settings,
                            );
                            await pushGenie(docId, {
                                id: docId,
                                version,
                                settings: {
                                    ...settings,
                                    padElements: items as GeniePadElementsSettingItem[],
                                },
                            });
                        } else {
                            await pushGenie(docId, {
                                id: docId,
                                version,
                                [key]: newItems,
                            });
                        }
                        dispatch(
                            fetchMyGenie(undefined, undefined, () => {
                                setIsPageBlocked(false);
                                unblock();
                                his.push({
                                    pathname: tx.pathname,
                                    state: tx.state,
                                });
                            }),
                        );
                        track('GENIE_CONFIGURE_SAVE');
                    } catch (e) {
                        console.error('pushGenie => ', e);
                        alert('Unable to save changes, please try again');
                        captureError(e as Error);
                        setIsPageBlocked(true);
                        track('ERROR', { on: 'saveGenie', what: (e as Error).message });
                    }
                },
                onCancel: (args) => {
                    if (args?.triggerCancel) {
                        return;
                    }

                    unblock();

                    his.push({
                        pathname: tx.pathname,
                        state: tx.state,
                    });
                    track("GENIE_DASHBOARD_DON'T_SAVE");
                    t.destroy();
                    return;
                },
                bodyStyle: { padding: '24px' },
            });

            t.update({
                okButtonProps: { className: 'primary-btn font-lato cursor-pointer' },
                cancelButtonProps: { className: 'neutral-btn font-lato cursor-pointer' },
            });

            setTimeout(() => {
                const el: HTMLDivElement = document.getElementsByClassName(
                    'ant-modal-wrap',
                )[0] as HTMLDivElement;
                el?.focus();
            }, 200);

            return false;
        });

        return () => {
            unblock();
        };
    });
    return null;
};

export default BlockHistoryGenie;
